/**
 * /me/notifications — lista e alarmeve S475; Push/Email mund të rregullohen te Cilësimet (jo në detajin e pajisjes).
 */
import { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import Layout from '../components/Layout';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';

import { BACKEND_URL } from '../config';

const SEVERITY_COLORS = {
  LOW: 'bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-300',
  MEDIUM: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300',
  HIGH: 'bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-300',
  CRITICAL: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300',
};

const Notification = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { user } = useAuth();
  const [events, setEvents] = useState([]);
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeOnly, setActiveOnly] = useState(false);

  const getToken = () => localStorage.getItem('access_token');
  const headers = () => ({ Authorization: `Bearer ${getToken()}` });

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') navigate('/me/settings');
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [navigate]);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${BACKEND_URL}/api/s475/devices`, { headers: headers() });
        setDevices(Array.isArray(res.data) ? res.data : []);
      } catch {
        setDevices([]);
      }
    })();
  }, []);

  const loadEvents = useCallback(async () => {
    setLoading(true);
    try {
      const q = new URLSearchParams({ limit: '250' });
      if (activeOnly) q.set('active_only', 'true');
      const res = await axios.get(`${BACKEND_URL}/api/s475/alarm-events?${q}`, { headers: headers() });
      const list = Array.isArray(res.data) ? res.data : [];
      list.sort((a, b) => new Date(b.timestamp || b.created_at || 0) - new Date(a.timestamp || a.created_at || 0));
      setEvents(list);
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || t('error'));
      setEvents([]);
    } finally {
      setLoading(false);
    }
  }, [activeOnly, t]);

  useEffect(() => {
    loadEvents();
  }, [loadEvents]);

  const acknowledgeAlarm = async (alarmId) => {
    try {
      await axios.patch(`${BACKEND_URL}/api/s475/alarm-events/${alarmId}/acknowledge`, {}, { headers: headers() });
      loadEvents();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || t('error'));
    }
  };

  const deviceLabel = (id) => {
    const d = devices.find((x) => String(x.id) === String(id));
    return d?.location_name || d?.label || id || '—';
  };

  const formatDate = (ts) => {
    if (!ts) return '—';
    const d = new Date(ts);
    return Number.isNaN(d.getTime()) ? String(ts) : d.toLocaleString('sq-AL', { dateStyle: 'short', timeStyle: 'short' });
  };

  const isSuper = user?.role === 'SUPERADMIN';

  return (
    <Layout>
      <div className="min-h-screen bg-gray-100 dark:bg-[#0f172a] p-4 pb-24 md:pb-6">
        <div className="max-w-lg mx-auto">
          <div className="flex items-center justify-between gap-3 mb-4">
            <h1 className="text-xl md:text-2xl font-semibold text-gray-900 dark:text-white">{t('notificationsAlarmsTitle')}</h1>
            <button
              type="button"
              onClick={() => navigate('/me/settings')}
              className="inline-flex items-center gap-2 text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white text-sm md:text-base"
            >
              <span>{t('back')}</span>
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
          </div>

          {isSuper && (
            <p className="text-xs text-gray-600 dark:text-gray-400 mb-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-white/80 dark:bg-gray-800/80 px-3 py-2">
              {t('notificationsSuperConfigHint')}{' '}
              <Link to="/me/settings" className="text-cyan-600 dark:text-cyan-400 underline font-medium">
                {t('systemSettings')}
              </Link>
            </p>
          )}

          <div className="flex items-center justify-between gap-2 mb-3">
            <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
              <input
                type="checkbox"
                checked={activeOnly}
                onChange={(e) => setActiveOnly(e.target.checked)}
                className="rounded border-gray-300"
              />
              {t('activeOnly')}
            </label>
            <Link to="/alarms" className="text-sm text-cyan-600 dark:text-cyan-400 hover:underline">
              {t('alarmSystem')} →
            </Link>
          </div>

          <div className="rounded-2xl overflow-hidden bg-white dark:bg-gray-800 shadow-lg border border-gray-200 dark:border-gray-700">
            {loading ? (
              <div className="p-8 text-center text-gray-500">{t('loading')}</div>
            ) : events.length === 0 ? (
              <div className="p-8 text-center text-gray-500 dark:text-gray-400">{t('noAlarmsInList')}</div>
            ) : (
              <ul className="divide-y divide-gray-200 dark:divide-gray-700">
                {events.map((a) => (
                  <li key={a.id} className="p-4">
                    <div className="flex flex-col gap-2">
                      <div className="flex flex-wrap items-center gap-2">
                        <span
                          className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${
                            SEVERITY_COLORS[a.severity] || SEVERITY_COLORS.MEDIUM
                          }`}
                        >
                          {a.severity}
                        </span>
                        {a.is_active && (
                          <span className="text-xs font-medium text-red-600 dark:text-red-400">{t('alarmActiveBadge')}</span>
                        )}
                        {a.acknowledged && (
                          <span className="text-xs text-gray-500 dark:text-gray-400">{t('acknowledged')}</span>
                        )}
                      </div>
                      <p className="font-medium text-gray-900 dark:text-white text-sm">{a.message || a.rule_key}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {deviceLabel(a.device_id)} · {a.rule_key} · {formatDate(a.timestamp)}
                      </p>
                      {a.is_active && !a.acknowledged && user?.role !== 'VIEWER' && (
                        <button
                          type="button"
                          onClick={() => acknowledgeAlarm(a.id)}
                          className="self-start mt-1 px-3 py-1.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium"
                        >
                          {t('acknowledge')}
                        </button>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Notification;
